import React from 'react';
import { FormattedMessage } from 'react-intl';
import moment from './moment';
import { formatInlineTableValue, renderInfoForType } from './helpers';

export const DEFAULT_FORM_NUMBER_COLUMN = '__form_number__';
export const DEFAULT_CREATED_DATE_COLUMN = '__created_date__';

export const previewValueKeys = [
  'label',
  'name',
  'value',
  'text',
  'title',
  'asset_registry_index',
  'asset_name',
  'display_value',
  'displayValue',
  'full_name',
  'fullname'
];

export const normalizeValue = value => String(value || '').trim().toLowerCase();

export const normalizePreviewValue = (value, depth = 0) => {
  if (depth > 5) return '...'; // Prevent circular references
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    return value
      .map(item => normalizePreviewValue(item, depth + 1))
      .filter(Boolean)
      .join(', ');
  }
  if (typeof value === 'object') {
    const preferredValue = previewValueKeys
      .map(key => value?.[key])
      .find(item => formatInlineTableValue(item).trim().length > 0);
    if (preferredValue !== undefined && preferredValue !== null) {
      return normalizePreviewValue(preferredValue, depth + 1);
    }
    const inlineValue = formatInlineTableValue(value);
    if (inlineValue) return inlineValue;
    return Object.values(value)
      .map(item => normalizePreviewValue(item, depth + 1))
      .filter(Boolean)
      .join(', ');
  }
  return '';
};

export const hasPreviewDisplayValue = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized.length > 0 && normalized !== 'undefined' && normalized !== 'null';
  }
  if (typeof value === 'number' || typeof value === 'boolean') return true;
  if (Array.isArray(value)) return value.some(item => hasPreviewDisplayValue(item));
  if (React.isValidElement(value)) return hasPreviewDisplayValue(value.props?.children);
  return false;
};

export const getDs6LinkedSchemaFieldsMap = (currentField, multiFieldId, allDataSources) => {
  const linkedForm = currentField?.multiple_fields?.[multiFieldId]?.form_field_data || {};
  const linkedFormKey = normalizeValue(linkedForm?.key || linkedForm?.form_key);
  if (!linkedFormKey) return new Map();

  const ds6Forms = allDataSources?.find(source => source?.name === 'ds6')?.forms || [];
  const selectedForm = ds6Forms?.find((formOption) => {
    const optionKey = normalizeValue(formOption?.form_key || formOption?.form?.form_key);
    return optionKey && optionKey === linkedFormKey;
  }) || {};

  const schemaFields = Array.isArray(selectedForm?.form?.schema?.fields)
    ? selectedForm.form.schema.fields
    : [];
  const fieldsMap = new Map();

  schemaFields.forEach((schemaField) => {
    if (schemaField?.id) {
      fieldsMap.set(schemaField.id, schemaField);
    }
  });

  return fieldsMap;
};

export const getDs6PreviewColumns = (fieldOrParent, multiFieldId) => {
  const field = multiFieldId ? fieldOrParent?.multiple_fields?.[multiFieldId] : fieldOrParent;
  if (field?.type?.key !== 'dataSource-ds6' && field?.type !== 'dataSource-ds6') return [];

  const selectedColumns = [
    ...(Array.isArray(field?.table_view_columns_to_display) ? field.table_view_columns_to_display : []),
    ...(Array.isArray(field?.settings?.table_view_columns_to_display)
      ? field.settings.table_view_columns_to_display
      : [])
  ];

  const dedupedColumns = [];
  const seenColumnIds = new Set();

  selectedColumns.forEach((column) => {
    const columnId = column?.value || column?.id || column?.key || '';
    if (!columnId || seenColumnIds.has(columnId)) return;
    seenColumnIds.add(columnId);
    dedupedColumns.push({ id: columnId, config: column });
  });

  return dedupedColumns;
};

export const getDs6ColumnTitle = (multiFieldId, columnId, columnConfig, currentField, allDataSources, linkedFieldsMap) => {
  if (columnId === DEFAULT_FORM_NUMBER_COLUMN) return <FormattedMessage id="form.list.table.order_number" defaultMessage="Form #" />;
  if (columnId === DEFAULT_CREATED_DATE_COLUMN) return <FormattedMessage id="forms.step2.created_date" defaultMessage="Created Date" />;

  const fieldsMap = linkedFieldsMap || getDs6LinkedSchemaFieldsMap(currentField, multiFieldId, allDataSources);
  const schemaField = fieldsMap?.get(columnId);
  return schemaField?.title || columnConfig?.label || columnConfig?.title || columnId;
};

export const getDs6ColumnValue = (options) => {
  const {
    entry,
    columnId,
    multiFieldId,
    currentField,
    allDataSources,
    renderInfoParams,
    linkedFieldsMap
  } = options;

  if (!entry) return '';

  if (columnId === DEFAULT_FORM_NUMBER_COLUMN) {
    if (!entry?.order_number) return '';
    const year = entry?.form_date ? moment(entry.form_date).format('YY') : '';
    return year ? `${year} - ${entry.order_number}` : `${entry.order_number}`;
  }

  if (columnId === DEFAULT_CREATED_DATE_COLUMN) {
    if (!entry?.form_date) return '';
    const dateFormat = renderInfoParams?.dateFormat || localStorage.getItem('dateFormat') || 'MM/DD/YYYY';
    const timeFormat = renderInfoParams?.timeFormat || localStorage.getItem('timeFormat') || 'HH:mm';
    return moment(entry.form_date).format(`${dateFormat} ${timeFormat}`);
  }

  const rawValue = entry?.data?.[columnId];
  let schemaField = null;
  if (linkedFieldsMap) {
    schemaField = linkedFieldsMap.get(columnId);
  } else if (multiFieldId) {
    schemaField = getDs6LinkedSchemaFieldsMap(currentField, multiFieldId, allDataSources)?.get(columnId);
  }


  if (schemaField) {
    const entrySchema = entry?.form_version__schema
      || entry?.form_version?.schema
      || entry?.form_parent?.schema
      || entry?.schema
      || null;

    const rendered = renderInfoForType(
      schemaField,
      rawValue,
      entry?.form_version || { schema: entrySchema },
      entrySchema,
      false, // badging
      renderInfoParams?.user, // profileDetails
      false, // fromBadgeSignatory
      renderInfoParams?.dateWithSeconds // sendWithSecond
    );
    if (hasPreviewDisplayValue(rendered)
      && (typeof rendered === 'string' || typeof rendered === 'number' || typeof rendered === 'boolean' || React.isValidElement(rendered))) {
      return rendered;
    }
  }

  return normalizePreviewValue(rawValue);
};
